<script setup lang="ts">
import { useBuildingBlockSelection } from '@/components/calculation-form/building-block-selection/composable/useBuildingBlockSelection'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import AvailableBuildingBlocksList from '@/components/calculation-form/building-block-selection/AvailableBuildingBlocksList.vue'
import SelectedBuildingBlocksList from '@/components/calculation-form/building-block-selection/SelectedBuildingBlocksList.vue'
import ProductSearchDialog from '@/components/app/ProductSearchDialog.vue'

const props = defineProps<CalculationFormMixinProps>()
const {
  isReadOnly,
  availableCategories,
  selectedBlocks,
  selectionNoticeTitle,
  selectionMissingMessages,
  selectionChangedMessages,
  showSearchMaterialDialog,
  handleAddBlock,
  handleRemoveBlock,
  handleDeleteBlock,
  handleAddMaterialFromSearch
} = useBuildingBlockSelection(props)
</script>

<template>
  <v-container fluid class="pa-6">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <h1 class="text-h4 mb-2">Building block selection</h1>
      </v-col>
      <v-col>
        <div class="text-body-2 text-medium-emphasis mb-4">
          <div>
            Select the building blocks (environmental profiles) to be used in your sustainability
            calculation. This section displays your customized building blocks and allows you to add
            default options from the chosen environmental database via <b>Search Material</b>.
          </div>
          <div>
            Add building blocks to your calculation using the <b>Add</b> button. Only the blocks
            shown in this overview can be used in the next calculation step.
          </div>
        </div>
        <v-icon size="small" class="mr-2">mdi-copyright</v-icon>
        <span class="text-body-2">Custom building block</span>
      </v-col>

      <v-col
        v-if="selectionMissingMessages.length || selectionChangedMessages.length"
        cols="12"
        class="pt-0"
      >
        <v-alert type="warning" variant="tonal" density="comfortable" class="mb-0">
          <div class="text-body-2">
            {{ selectionNoticeTitle }}
          </div>
          <div
            v-for="(message, index) in selectionMissingMessages"
            :key="`selection-switch-missing-${index}`"
            class="text-body-2 mt-1"
          >
            {{ message }}
          </div>
          <div
            v-for="(message, index) in selectionChangedMessages"
            :key="`selection-switch-changed-${index}`"
            class="text-body-2 mt-1"
          >
            {{ message }}
          </div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Two Columns -->
    <v-row>
      <v-col cols="12" md="6">
        <AvailableBuildingBlocksList
          :categories="availableCategories"
          :readonly="isReadOnly"
          @add-block="handleAddBlock"
          @delete-block="handleDeleteBlock"
        />

        <!-- Search Material Button -->
        <v-btn
          variant="flat"
          prepend-icon="mdi-magnify"
          class="mt-4"
          :disabled="isReadOnly"
          @click="showSearchMaterialDialog = true"
        >
          Search Material
        </v-btn>
      </v-col>

      <v-col cols="12" md="6">
        <SelectedBuildingBlocksList
          :selected-blocks="selectedBlocks"
          :readonly="isReadOnly"
          @remove-block="handleRemoveBlock"
        />
      </v-col>
    </v-row>

    <!-- Search Material Dialog -->
    <ProductSearchDialog
      v-model="showSearchMaterialDialog"
      title="Search Material"
      subtitle="Search for a material from the database and create a building block from it."
      search-label="Enter material name"
      no-match-label="No material matching"
      empty-search-label="Type a material name to search"
      confirm-text="Add"
      @select="handleAddMaterialFromSearch"
    />
  </v-container>
</template>
